import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface ErrorInfo {
  id: string;
  title: string;
  message: string;
  type: 'error';
}

interface ErrorContextType {
  errors: ErrorInfo[];
  addError: (error: Omit<ErrorInfo, 'id'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
}

export const useErrorContext = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error(
      'useErrorContext must be used within an ErrorProvider',
    );
  }
  return context;
};

const ErrorContext = createContext<ErrorContextType | undefined>(
  undefined,
);

export const ErrorContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  const addError = useCallback((error: Omit<ErrorInfo, 'id'>) => {
    const id = `error-${uuidv4()}`;
    const newError: ErrorInfo = { ...error, id };

    setErrors((prev) => [...prev, newError]);
    return id;
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <ErrorContext.Provider
      value={{ errors, addError, removeError, clearErrors }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

export { ErrorContext };
export default ErrorContextProvider;
